import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { taskApi, checkinApi, userApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  MapPin,
  CalendarDays,
  CheckCircle,
  XCircle,
  Navigation,
  Phone,
  Clock,
  RefreshCw
} from 'lucide-react';

export default function MyTasksPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(null);

  const fetchTasks = async () => {
    try {
      const res = await taskApi.getAll();
      setTasks(res.data);
    } catch (err) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
    // Update location periodically
    const locationInterval = setInterval(() => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              await userApi.updateLocation(user.id, {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });
            } catch (err) {
              // Silent fail for location updates
            }
          },
          () => {}
        );
      }
    }, 60000); // Every minute

    return () => clearInterval(locationInterval);
  }, [user?.id]);

  const handleCheckIn = async (taskId, type) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }

    setCheckingIn(taskId);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await checkinApi.create({
            task_id: taskId,
            check_type: type,
            location: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            },
          });
          toast.success(`${type === 'checkin' ? 'Checked in' : 'Checked out'} successfully!`);
          fetchTasks();
        } catch (err) {
          toast.error(`Failed to ${type}`);
        } finally {
          setCheckingIn(null);
        }
      },
      (err) => {
        toast.error('Could not get your location');
        setCheckingIn(null);
      }
    );
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-gray-100 text-gray-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="my-tasks-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>My Tasks</h1>
            <p className="text-sm text-gray-500 mt-1">
              {tasks.length} total tasks • {inProgressTasks.length} in progress
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchTasks} data-testid="refresh-tasks-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-gray-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>{pendingTasks.length}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-semibold text-blue-700" style={{ fontFamily: 'Outfit' }}>{inProgressTasks.length}</p>
              <p className="text-sm text-blue-600">In Progress</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-semibold text-green-700" style={{ fontFamily: 'Outfit' }}>{completedTasks.length}</p>
              <p className="text-sm text-green-600">Completed</p>
            </CardContent>
          </Card>
        </div>

        {/* In Progress Task (Priority) */}
        {inProgressTasks.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-medium text-gray-900" style={{ fontFamily: 'Outfit' }}>Currently Working On</h2>
            {inProgressTasks.map((task) => (
              <Card key={task.id} className="border-blue-200 bg-blue-50/50" data-testid={`in-progress-task-${task.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{task.title}</h3>
                        <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                      </div>
                      <p className="text-gray-600 mt-1">{task.customer_name}</p>
                      
                      <div className="flex items-center gap-4 mt-3 text-sm">
                        <span className="flex items-center gap-1 text-gray-500">
                          <MapPin className="w-4 h-4" />
                          {task.customer_location?.address || 'No address'}
                        </span>
                        {task.customer_phone && (
                          <a
                            href={`tel:${task.customer_phone}`}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                          >
                            <Phone className="w-4 h-4" />
                            Call
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        className="bg-red-600 hover:bg-red-700 text-white checkin-btn"
                        onClick={() => handleCheckIn(task.id, 'checkout')}
                        disabled={checkingIn === task.id}
                        data-testid={`checkout-btn-${task.id}`}
                      >
                        {checkingIn === task.id ? (
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <XCircle className="w-4 h-4 mr-2" />
                        )}
                        Check Out
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const loc = task.customer_location;
                          if (loc?.lat && loc?.lng) {
                            window.open(`https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`, '_blank');
                          }
                        }}
                        data-testid={`navigate-btn-${task.id}`}
                      >
                        <Navigation className="w-4 h-4 mr-2" />
                        Navigate
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pending Tasks */}
        {pendingTasks.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-medium text-gray-900" style={{ fontFamily: 'Outfit' }}>Pending Tasks</h2>
            {pendingTasks.map((task) => (
              <Card key={task.id} className="border-gray-200 hover:border-blue-200 transition-all" data-testid={`pending-task-${task.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-gray-900">{task.title}</h3>
                        <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{task.customer_name}</p>
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {task.customer_location?.address || 'No address'}
                        </span>
                        {task.due_date && (
                          <span className="flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" />
                            {task.due_date}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        className="bg-green-600 hover:bg-green-700 text-white checkin-btn"
                        onClick={() => handleCheckIn(task.id, 'checkin')}
                        disabled={checkingIn === task.id}
                        data-testid={`checkin-btn-${task.id}`}
                      >
                        {checkingIn === task.id ? (
                          <Clock className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Check In
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/tasks/${task.id}`)}
                        data-testid={`view-task-btn-${task.id}`}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Completed Tasks */}
        {completedTasks.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-medium text-gray-900" style={{ fontFamily: 'Outfit' }}>Completed Today</h2>
            {completedTasks.slice(0, 5).map((task) => (
              <Card key={task.id} className="border-gray-200 bg-gray-50" data-testid={`completed-task-${task.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-700">{task.title}</p>
                      <p className="text-sm text-gray-500">{task.customer_name}</p>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Completed
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading tasks...</p>
          </div>
        ) : tasks.length === 0 ? (
          <Card className="border-gray-200">
            <CardContent className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900" style={{ fontFamily: 'Outfit' }}>All caught up!</h3>
              <p className="text-gray-500 mt-2">No tasks assigned to you at the moment.</p>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </MainLayout>
  );
}
